import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { GoChevronDown } from "react-icons/go";
import ReactDOM from "react-dom";
import { Language } from "./Languages";

interface LanguageSwitchInlineProps {
  languages?: Language[];
  className?: string;
}

function LanguageSwitchInline(props: Readonly<LanguageSwitchInlineProps>) {
  const { languages, className } = props;
  const [t, i18n] = useTranslation();
  const [language, setLanguage] = useState(i18n.language);
  const [isCurrentMenuOpen, setIsCurrentMenuOpen] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const navigate = useNavigate();
  const location = useLocation();

  const buttonRef = useRef<HTMLButtonElement>(null);

  // Gestisce quando il dropdown dovrebbe essere renderizzato
  useEffect(() => {
    if (isCurrentMenuOpen) {
      setShouldRender(true);
    } else {
      // Delay per permettere all'animazione di completarsi
      const timer = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isCurrentMenuOpen]);

  // Chiude il dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        // Verifica se il click è avvenuto nel dropdown
        const dropdown = document.getElementById('language-dropdown');
        if (!dropdown || !dropdown.contains(event.target as Node)) {
          setIsCurrentMenuOpen(false);
        }
      }
    };

    if (isCurrentMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCurrentMenuOpen]);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    
    // Update URL with language parameter
    const searchParams = new URLSearchParams(location.search);
    if (lng === 'it') {
      // Remove lang parameter for default Italian
      searchParams.delete('lang');
    } else {
      searchParams.set('lang', lng);
    }
    
    const newSearch = searchParams.toString();
    const newPath = location.pathname + (newSearch ? `?${newSearch}` : '');
    navigate(newPath, { replace: true });
  };

  // Calculate dropdown position
  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8, // 8px spacing
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Get selected language object
  const selectedLanguage = languages?.find((l) => l.Value === language);

  return (
    <div
      className="w-full relative"
      role="none"
    >
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          updateDropdownPosition();
          setIsCurrentMenuOpen(!isCurrentMenuOpen);
        }}
        className={
          "px-6 py-[2px] rounded-md bg-lightbg-headerPrimary  shadow-md transition duration-200 hover:bg-lightbg-headerSecondary hover:scale-105 whitespace-nowrap w-full flex items-center min-h-[40px] " +
          (className ?? "")
        }
        type="button"
        aria-expanded={isCurrentMenuOpen ? "true" : "false"}
        aria-label={`${t('language')}: ${selectedLanguage?.Name}`}
      >
        <span className="flex-1 text-left">
          {selectedLanguage?.Name}
        </span>
        <GoChevronDown 
          className={`ml-2 transition-transform duration-200 ${
            isCurrentMenuOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>
      
      {/* Dropdown renderizzato con portale */}
      {typeof window !== "undefined" && shouldRender &&
        ReactDOM.createPortal(
          <div 
            className={`fixed z-[2147483647] min-w-[140px] ${
              isCurrentMenuOpen ? 'animate-[slideDown_0.3s_ease-out_forwards]' : 'animate-[slideUp_0.2s_ease-in_forwards]'
            }`}
            style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              minWidth: `${Math.max(dropdownPosition.width, 140)}px`,
              zIndex: 2147483647
            }}
            role="none"
            id="language-dropdown"
          >
            <div
              className="overflow-hidden flex flex-col rounded-md shadow-2xl border-lightbg-darkGrey border-solid border-[1px] bg-lightbg-headerPrimary"
            >
              {languages
                ?.filter((lang) => lang.Value !== language)
                .map((lang, index, filteredArray) => (
                  <button
                    key={lang.Name}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsCurrentMenuOpen(false);
                      setLanguage(lang.Value);
                      changeLanguage(lang.Value);
                    }}
                    className={`
                      text-font-lightGrey font-semibold
                      w-full text-left px-6 py-3  
                      hover:bg-lightbg-headerSecondary transition-colors duration-200
                      ${index === 0 ? 'rounded-t-md' : ''}
                      ${index === filteredArray.length - 1 ? 'rounded-b-md' : ''}
                    `}
                  >
                    {lang.Name}
                  </button>
                ))}
            </div>
          </div>,
          document.body
        )
      }
    </div>
  );
}

export default LanguageSwitchInline;
